import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Link, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerAccountGroupConfig = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('customer_account_group_config');
  const [viewItem, setViewItem] = useState(null);
  const [configurations, setConfigurations] = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  const [numberRanges, setNumberRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [formData, setFormData] = useState({
    account_group_id: '',
    number_range_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configsRes, groupsRes, rangesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customer-account-group-configs`),
        axios.get(`${BACKEND_URL}/api/customer-account-groups`),
        axios.get(`${BACKEND_URL}/api/customer-number-ranges`)
      ]);
      
      setConfigurations(configsRes.data);
      setAccountGroups(groupsRes.data);
      setNumberRanges(rangesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableAccountGroups = () => {
    // In create mode: show all account groups (validation happens on backend)
    // In edit mode: only show current group since we can't change it
    if (editMode && currentConfig) {
      return accountGroups.filter(g => g.account_group_id === currentConfig.account_group_id);
    }
    // For create mode, show all groups and let backend handle validation
    return accountGroups;
  };

  const getAvailableNumberRanges = () => {
    // Show ALL number ranges in both create and edit modes
    // Backend handles validation for duplicate assignments
    return numberRanges;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.account_group_id || !formData.number_range_id) {
      toast.error('Please select both Account Group and Number Range');
      return;
    }

    try {
      if (editMode) {
        await axios.put(
          `${BACKEND_URL}/api/customer-account-group-configs/${currentConfig.account_group_id}`,
          { number_range_id: formData.number_range_id }
        );
        toast.success('Configuration updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customer-account-group-configs`, formData);
        toast.success('Configuration created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const handleEdit = (config) => {
    setCurrentConfig(config);
    setFormData({
      account_group_id: config.account_group_id,
      number_range_id: config.number_range_id
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({ account_group_id: '', number_range_id: '' });
    setCurrentConfig(null);
    setEditMode(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  const getAccountGroupDescription = (id) => {
    const group = accountGroups.find(g => g.account_group_id === id);
    return group?.description || id;
  };

  const getNumberRangeDescription = (id) => {
    const range = numberRanges.find(r => r.number_range_id === id);
    if (!range) return id;
    return range.description ? `${id} - ${range.description}` : id;
  };

  const availableGroups = getAvailableAccountGroups();
  const availableRanges = getAvailableNumberRanges();

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Link className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Account Group Configuration</h1>
                <p className="text-sm text-gray-600">Assign number ranges to customer account groups</p>
              </div>
            </div>
            {writable && (<Button
              onClick={() => setShowDialog(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Configuration
            </Button>)}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
              <p className="text-sm text-gray-600">Loading configurations...</p>
            </div>
          ) : configurations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Link className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No configurations yet</h3>
              <p className="text-sm text-gray-600 mb-4">
                Assign number ranges to account groups to enable customer creation
              </p>
              {writable && (<Button onClick={() => setShowDialog(true)} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Configuration
              </Button>)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Group</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Number Range</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {configurations.map((config) => (
                    <tr key={config.account_group_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{config.account_group_id}</span>
                      </td>
                      <td className="px-6 py-4">{getAccountGroupDescription(config.account_group_id)}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded font-medium">
                          {config.number_range_id}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <RecordActions doctype="customer_account_group_config" onView={() => setViewItem(config)} onEdit={() => handleEdit(config)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Configuration' : 'Add Configuration'}
                </h2>
                <button onClick={handleDialogClose} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Customer Account Group *</Label>
                  <select
                    value={formData.account_group_id}
                    onChange={(e) => setFormData({ ...formData, account_group_id: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                    disabled={editMode}
                  >
                    <option value="">Select Account Group</option>
                    {editMode ? (
                      <option value={currentConfig.account_group_id}>
                        {currentConfig.account_group_id} - {getAccountGroupDescription(currentConfig.account_group_id)}
                      </option>
                    ) : (
                      availableGroups.map(group => (
                        <option key={group.account_group_id} value={group.account_group_id}>
                          {group.account_group_id} - {group.description}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <Label>Customer Number Range *</Label>
                  <select
                    value={formData.number_range_id}
                    onChange={(e) => setFormData({ ...formData, number_range_id: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Number Range</option>
                    {availableRanges.map(range => (
                      <option key={range.number_range_id} value={range.number_range_id}>
                        {getNumberRangeDescription(range.number_range_id)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-xs text-orange-700">
                    <strong>Note:</strong> {editMode 
                      ? 'You can change the number range assignment for this account group.'
                      : 'Each account group can only be assigned to one number range, and each number range can only be assigned to one account group.'}
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default CustomerAccountGroupConfig;
